import { useState, useEffect } from 'react'
import { Globe, Check } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface LanguageModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Language = {
  code: string
  name: string
  nativeName: string
}

const LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '简体中文' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia' },
]

export function LanguageModal({ open, onOpenChange }: LanguageModalProps) {
  const [selectedLanguage, setSelectedLanguage] = useState('en')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    // Load saved language preference
    const savedLanguage = localStorage.getItem('language') || 'en'
    setSelectedLanguage(savedLanguage)
  }, [open])

  const applyLanguage = (languageCode: string) => {
    setSelectedLanguage(languageCode)
    localStorage.setItem('language', languageCode)

    // Update document language attribute
    document.documentElement.lang = languageCode

    // In a real app, you would trigger i18n to reload translations
    // For now, we'll just show a message that the language is saved
  }

  const filteredLanguages = LANGUAGES.filter((lang) =>
    lang.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lang.nativeName.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Language</DialogTitle>
          <DialogDescription>
            Choose your preferred language for KLASSIC
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="py-2">
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search languages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Language List */}
        <div className="overflow-y-auto max-h-[50vh] -mx-1 px-1">
          <div className="space-y-1">
            {filteredLanguages.map((language) => {
              const isSelected = selectedLanguage === language.code

              return (
                <button
                  key={language.code}
                  onClick={() => applyLanguage(language.code)}
                  className={`
                    w-full flex items-center justify-between p-3 rounded-lg transition-colors
                    ${
                      isSelected
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-accent'
                    }
                  `}
                >
                  <div className="text-left">
                    <p className="font-medium">{language.nativeName}</p>
                    <p className="text-sm text-muted-foreground">{language.name}</p>
                  </div>
                  {isSelected && <Check className="h-5 w-5" />}
                </button>
              )
            })}

            {filteredLanguages.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Globe className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No languages found</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
